/* bs-product-carousel — carousel + add-to-cart fetch */
/* Patrones: nav siempre visible (decision Lead), disabled state segun scroll position.
   Add-to-cart hace POST a /cart/add.js y dispara cart:update event para que el cart bubble
   y el cart drawer de Horizon se actualicen sin recargar la pagina. */

(function () {
  'use strict';

  class BsProductCarousel {
    constructor(section) {
      this.section = section;
      this.scroller = section.querySelector('[data-bs-product-carousel-scroller]');
      this.row = this.scroller ? this.scroller.querySelector('.bs-product-carousel__row') : null;
      this.counter = section.querySelector('[data-bs-product-carousel-counter]');
      this.prevBtn = section.querySelector('[data-direction="prev"]');
      this.nextBtn = section.querySelector('[data-direction="next"]');
      this.cards = this.scroller ? this.scroller.querySelectorAll('.bs-product-carousel__card') : [];
      this.totalCards = this.cards.length;
      this.forms = section.querySelectorAll('[data-bs-product-carousel-form]');

      if (!this.scroller || this.totalCards === 0) {
        return;
      }

      this.bindEvents();
      this.checkOverflow();
      this.updateUi();
    }

    bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollByCard(-1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.scrollByCard(1));
      }
      this.scroller.addEventListener('scroll', () => this.updateUi(), { passive: true });

      window.addEventListener(
        'resize',
        () => {
          this.checkOverflow();
          this.updateUi();
        },
        { passive: true }
      );

      // Add-to-cart: intercepta submit del form y hace fetch a /cart/add.js.
      // Si el fetch falla, deja que el form post nativo redirija a /cart (graceful fallback).
      this.forms.forEach((form) => {
        form.addEventListener('submit', (event) => this.handleAddToCart(event, form));
      });
    }

    // Detecta si el contenido del scroller desborda. Desktop: ≤5 cards = layout pixel-perfect
    // (space-between, sin scroll real); ≥6 cards = data-overflow="true" -> CSS aplica gap 20 +
    // mandatory snap. En mobile/tablet siempre overflow.
    checkOverflow() {
      if (!this.scroller) return;
      const tolerance = 1;
      const isOverflowing = this.scroller.scrollWidth > this.scroller.clientWidth + tolerance;
      if (isOverflowing) {
        this.section.dataset.overflow = 'true';
      } else {
        this.section.removeAttribute('data-overflow');
      }
    }

    getStep() {
      // Step = card width + gap real (lee del DOM para soportar viewport changes)
      const cardWidth = this.cards[0].offsetWidth;
      let gap = 21;
      if (this.row) {
        const computed = window.getComputedStyle(this.row);
        const parsedGap = parseFloat(computed.gap || computed.columnGap);
        if (!Number.isNaN(parsedGap)) {
          gap = parsedGap;
        }
      }
      return cardWidth + gap;
    }

    scrollByCard(direction) {
      const step = this.getStep();
      const prefersReduced =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = prefersReduced ? 'auto' : 'smooth';

      // Calculo explicito del target para evitar que mandatory snap vuelva al snap-start anterior
      // cuando el ultimo card tiene snap-align: end (gap pequeno entre penultimo y ultimo target).
      // scrollBy + clamp al maxScroll fuerza al browser a ir al final exacto en el ultimo click.
      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
      const currentScrollLeft = this.scroller.scrollLeft;

      // Cards evenly spaced (step = card-width + gap uniforme) → currentScrollLeft - step
      // siempre cae en el snap-start anterior, incluso desde snap-end del ultimo card.
      let target = direction > 0 ? currentScrollLeft + step : currentScrollLeft - step;

      // Clamp a [0, maxScroll]. En el ultimo click hacia delante, target > maxScroll → maxScroll
      // exacto, donde el snap-align: end del ultimo card alinea su edge derecho.
      target = Math.max(0, Math.min(target, maxScroll));
      this.scroller.scrollTo({ left: target, behavior });
    }

    updateUi() {
      const scrollLeft = this.scroller.scrollLeft;
      const scrollRight = this.scroller.scrollWidth - this.scroller.clientWidth - scrollLeft;

      if (this.prevBtn) {
        const prevDisabled = scrollLeft < 1;
        this.prevBtn.disabled = prevDisabled;
        // A11y: aria-disabled explicito para AT que no leen disabled HTML attr de manera consistente
        this.prevBtn.setAttribute('aria-disabled', prevDisabled ? 'true' : 'false');
      }
      if (this.nextBtn) {
        const nextDisabled = scrollRight < 1;
        this.nextBtn.disabled = nextDisabled;
        this.nextBtn.setAttribute('aria-disabled', nextDisabled ? 'true' : 'false');
      }

      if (this.counter) {
        // Counter: muestra "indice / total positions" donde total = cards visibles + scroll positions
        const step = this.getStep();
        const cardsVisible =
          step > 0 ? Math.max(1, Math.floor(this.scroller.clientWidth / step + 0.1)) : 1;
        const totalPositions = Math.max(1, this.totalCards - cardsVisible + 1);

        // Counter por cards-a-la-izquierda + caso especial fin. Antes usabamos progress lineal
        // (scrollLeft / maxScroll) pero con snap-align: end en el ultimo card las posiciones de
        // snap NO son uniformes (gap menor entre penultimo y ultimo) → progress mappeaba mal.
        // Nueva logica: idx = floor(scrollLeft / step) + 1 (cards completos a la izquierda + 1).
        // Cuando scrollLeft >= maxScroll - 2 (estamos al final), idx = totalPositions.
        const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
        let idx;
        if (maxScroll <= 0) {
          idx = 1;
        } else if (scrollLeft >= maxScroll - 2) {
          idx = totalPositions;
        } else {
          idx = Math.round(scrollLeft / step) + 1;
        }
        const clampedIdx = Math.max(1, Math.min(idx, totalPositions));
        this.counter.textContent = clampedIdx + ' / ' + totalPositions;
      }
    }

    /**
     * Add-to-cart handler. POST a /cart/add.js + fetch /cart.js para obtener el cart
     * actualizado, despues dispatch cart:update event (matches Horizon's ThemeEvents.cartUpdate).
     * Esto sincroniza el cart bubble del header y el drawer si esta abierto.
     */
    async handleAddToCart(event, form) {
      event.preventDefault();
      const btn = form.querySelector('.bs-product-carousel__add-btn');
      if (!btn || btn.disabled) return;

      const formData = new FormData(form);
      btn.dataset.loading = 'true';
      btn.disabled = true;

      try {
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });

        if (!addResponse.ok) {
          const errorBody = await addResponse.json().catch(() => ({}));
          this.dispatchCartError(form, errorBody);

          // Mensaje legible: prioriza description (humano), fallback a message, fallback genericos.
          const rawDesc = errorBody.description || errorBody.message || '';
          const lowDesc = rawDesc.toLowerCase();

          // Detectar error de stock especificamente (multi-idioma).
          const isStockError =
            addResponse.status === 422 &&
            (lowDesc.includes('available') ||
              lowDesc.includes('stock') ||
              lowDesc.includes('disponible') ||
              lowDesc.includes('agotad') ||
              lowDesc.includes('inventory'));

          if (isStockError) {
            // Stock case: el boton "Sin stock" disabled YA es feedback suficiente en la card.
            this.markAsOutOfStock(btn);
          } else {
            // Otros errores: mostrar mensaje inline DENTRO de la card (bajo el boton),
            // con la descripcion real del servidor para que el usuario entienda por que fallo.
            this.showInlineError(form, rawDesc || 'No se pudo añadir el producto.');
          }
          return;
        }

        const addedItem = await addResponse.json();

        // Fetch cart actualizado para event payload
        let cart = null;
        try {
          const cartResponse = await fetch('/cart.js', {
            headers: { Accept: 'application/json' },
          });
          if (cartResponse.ok) {
            cart = await cartResponse.json();
          }
        } catch (e) {
          // sigue: el dispatch sin cart object aun activa el cart bubble fetch refresh
        }

        // Visual feedback: "Añadido" durante 1.5s
        btn.dataset.added = 'true';
        const originalLabel = btn.querySelector('.bs-product-carousel__add-label');
        const originalText = originalLabel ? originalLabel.textContent : 'Añadir';
        if (originalLabel) originalLabel.textContent = 'Añadido';

        // Dispatch evento para que el resto del theme (cart icon, drawer) se actualice
        this.dispatchCartUpdate(form, cart, addedItem);

        setTimeout(() => {
          btn.removeAttribute('data-added');
          if (originalLabel) originalLabel.textContent = originalText;
        }, 1500);
      } catch (error) {
        // Network error: mensaje inline en la card. El boton vuelve a su estado normal
        // para que el usuario pueda reintentar despues de revisar la red.
        console.error('[bs-product-carousel] add-to-cart failed:', error);
        this.showInlineError(form, 'Error de conexión. Inténtalo de nuevo.');
      } finally {
        btn.removeAttribute('data-loading');
        // Solo re-enable si NO se marco como out-of-stock (ese es disabled persistente).
        if (!btn.classList.contains('bs-product-carousel__add-btn--out-of-stock')) {
          btn.disabled = false;
        }
      }
    }

    /**
     * Error inline dentro de la card (bajo el form). Crea o reutiliza un <p> con role=alert
     * para que screen readers anuncien el error. Auto-desaparece despues de 4s.
     * Aparece DENTRO de la card del producto que fallo, no flotando — feedback contextual.
     */
    showInlineError(form, message) {
      const card = form.closest('.bs-product-carousel__card');
      if (!card) return;

      // Reutilizar contenedor existente o crear uno con estructura: icono + texto
      let errorEl = card.querySelector('.bs-product-carousel__form-error');
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.className = 'bs-product-carousel__form-error';
        errorEl.setAttribute('role', 'alert');
        errorEl.setAttribute('aria-live', 'assertive');

        // Icono SVG inline (warning triangle) — heredas currentColor
        const iconWrap = document.createElement('span');
        iconWrap.className = 'bs-product-carousel__form-error-icon';
        iconWrap.setAttribute('aria-hidden', 'true');
        iconWrap.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';

        const textEl = document.createElement('span');
        textEl.className = 'bs-product-carousel__form-error-text';

        errorEl.appendChild(iconWrap);
        errorEl.appendChild(textEl);
        form.insertAdjacentElement('afterend', errorEl);
      }

      const textEl = errorEl.querySelector('.bs-product-carousel__form-error-text');
      if (textEl) textEl.textContent = message;
      errorEl.classList.add('bs-product-carousel__form-error--visible');

      if (errorEl._dismissTimer) clearTimeout(errorEl._dismissTimer);
      errorEl._dismissTimer = setTimeout(() => {
        errorEl.classList.remove('bs-product-carousel__form-error--visible');
      }, 4500);
    }

    /**
     * Marca el boton como agotado: cambia label a "Sin stock", aplica estado disabled visual
     * (mismo estilo que el server-rendered "Agotado") y bloquea futuros submits.
     * Persiste hasta que el usuario refresque la pagina (no recuperamos stock dinamicamente).
     */
    markAsOutOfStock(btn) {
      const label = btn.querySelector('.bs-product-carousel__add-label');
      if (label) label.textContent = 'Sin stock';

      // Si habia icono, escondelo (matching el server-render del estado "Agotado")
      const icon = btn.querySelector('.bs-product-carousel__add-icon');
      if (icon) icon.style.display = 'none';

      btn.disabled = true;
      btn.classList.add('bs-product-carousel__add-btn--disabled');
      btn.classList.add('bs-product-carousel__add-btn--out-of-stock');
      btn.setAttribute('aria-label', 'Sin stock disponible');

      // Limpia data-added si estaba (caso edge: añade ultima unidad y al siguiente click no hay)
      btn.removeAttribute('data-added');
    }

    /**
     * Dispatch cart:update event compatible con ThemeEvents de Horizon.
     * El cart-icon, cart-drawer y otros componentes que escuchan este evento se refrescan.
     */
    dispatchCartUpdate(form, cart, addedItem) {
      const variantInput = form.querySelector('input[name="id"]');
      const variantId = variantInput ? variantInput.value : undefined;

      // Construir CustomEvent con la misma forma que CartUpdateEvent de events.js
      const detail = {
        resource: cart,
        sourceId: this.section.dataset.sectionId || 'bs-product-carousel',
        data: {
          source: 'bs-product-carousel',
          itemCount: cart ? cart.item_count : undefined,
          variantId: variantId,
          didError: false,
          addedItem: addedItem,
        },
      };

      // Disparar tanto desde document como desde la section: bubbling cubre ambos casos
      const evt = new CustomEvent('cart:update', { bubbles: true, detail });
      document.dispatchEvent(evt);
    }

    dispatchCartError(form, errorBody) {
      const variantInput = form.querySelector('input[name="id"]');
      const variantId = variantInput ? variantInput.value : undefined;
      const detail = {
        sourceId: this.section.dataset.sectionId || 'bs-product-carousel',
        data: {
          message: errorBody.message,
          description: errorBody.description,
          errors: errorBody.errors,
          variantId: variantId,
        },
      };
      document.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail }));
    }
  }

  function init(root) {
    const sections = (root || document).querySelectorAll('.bs-product-carousel');
    sections.forEach((section) => {
      if (section.__bsProductCarouselInit) return;
      section.__bsProductCarouselInit = true;
      new BsProductCarousel(section);
    });
  }


  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
    });
  } else {
    init();
  }

  // Re-init cuando el customizer recarga la section
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.bs-product-carousel');
    if (section) {
      section.__bsProductCarouselInit = false;
      init(event.target);
    }
  });
})();
